import { useMedplum } from '@medplum/react';
import { useEffect, useState } from 'react';
import type { CarePlan, Condition, EpisodeOfCare } from '@medplum/fhirtypes';
import { TEMPLATE_CATEGORY_SYSTEM } from '@unify/fhir-model/constants';
import {
  getCarePlanEpisodeId,
  isActiveTreatmentPlanCarePlan,
} from '@unify/fhir-model/resources/treatment-plan-careplan';

function formatConditionLabel(condition: Condition): string {
  const coding = condition.code?.coding?.[0];
  const code = coding?.code ?? condition.code?.text;
  const display = coding?.display ?? condition.code?.text;
  if (code && display && display !== code) {
    return `${code} - ${display}`;
  }
  return code ?? display ?? '—';
}

function formatDiagnosisLabels(conditions: Condition[]): string | undefined {
  const active = conditions.filter((c) =>
    c.clinicalStatus?.coding?.some((coding) => coding.code === 'active'),
  );
  if (active.length === 0) return undefined;
  return active.map(formatConditionLabel).join('; ');
}

function sortedDiagnosisEntries(eoc: EpisodeOfCare) {
  return [...(eoc.diagnosis ?? [])].sort(
    (a, b) => (a.rank ?? Number.MAX_SAFE_INTEGER) - (b.rank ?? Number.MAX_SAFE_INTEGER),
  );
}

function fallbackLabelsFromEpisodeReferences(eoc: EpisodeOfCare): string | undefined {
  const labels = sortedDiagnosisEntries(eoc)
    .map((entry) => entry.condition?.display?.trim())
    .filter((label): label is string => Boolean(label));
  return labels.length > 0 ? labels.join('; ') : undefined;
}

function fallbackLabelsFromCarePlanAddresses(plan: CarePlan): string | undefined {
  const labels = (plan.addresses ?? [])
    .map((address) => address.display?.trim())
    .filter((label): label is string => Boolean(label));
  return labels.length > 0 ? labels.join('; ') : undefined;
}

/**
 * Episode diagnosis for display — prefers the active treatment-plan CarePlan.addresses
 * Conditions, then EpisodeOfCare.diagnosis[] (kept in sync from that CarePlan).
 */
export function useEpisodeDiagnosisDisplay(
  episodeId: string | undefined,
): { diagnosis: string | undefined; loading: boolean } {
  const medplum = useMedplum();
  const [diagnosis, setDiagnosis] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!episodeId) {
      setDiagnosis(undefined);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    void (async () => {
      try {
        const eoc = await medplum.readResource('EpisodeOfCare', episodeId);
        const patientRef = eoc.patient?.reference;
        if (patientRef) {
          const plans = await medplum.searchResources('CarePlan', {
            subject: patientRef,
            _tag: `${TEMPLATE_CATEGORY_SYSTEM}|treatment-planning`,
            _count: '50',
          });
          const activePlan = plans
            .filter(
              (plan) =>
                isActiveTreatmentPlanCarePlan(plan) && getCarePlanEpisodeId(plan) === episodeId,
            )
            .sort((a, b) => (b.created ?? '').localeCompare(a.created ?? ''))[0];
          if (activePlan?.addresses?.length) {
            const conditions = await Promise.all(
              (activePlan.addresses ?? []).map(async (address) => {
                const id = address.reference?.replace(/^Condition\//, '');
                if (!id) return undefined;
                try {
                  return await medplum.readResource('Condition', id);
                } catch {
                  return undefined;
                }
              }),
            );
            const fromCarePlan =
              formatDiagnosisLabels(conditions.flatMap((condition) => (condition ? [condition] : [])))
              ?? fallbackLabelsFromCarePlanAddresses(activePlan);
            if (fromCarePlan) {
              if (!cancelled) setDiagnosis(fromCarePlan);
              return;
            }
          }
        }

        const entries = sortedDiagnosisEntries(eoc);
        if (entries.length === 0) {
          if (!cancelled) setDiagnosis(undefined);
          return;
        }

        const conditions = await Promise.all(
          entries.map(async (entry) => {
            const id = entry.condition?.reference?.replace(/^Condition\//, '');
            if (!id) return undefined;
            try {
              return await medplum.readResource('Condition', id);
            } catch {
              return undefined;
            }
          }),
        );

        const resolved = formatDiagnosisLabels(conditions.flatMap((condition) => (condition ? [condition] : [])));
        const label = resolved ?? fallbackLabelsFromEpisodeReferences(eoc);
        if (!cancelled) setDiagnosis(label);
      } catch {
        if (!cancelled) setDiagnosis(undefined);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [episodeId, medplum]);

  return { diagnosis, loading };
}
